/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  ChartLegend,
  RechartsTooltip as Tooltip,
} from "@capacit-com/lara-law-shared-ui";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@capacit-com/lara-law-shared-ui";
import { ChartConfig, ChartContainer } from "@capacit-com/lara-law-shared-ui";

interface UserGraphStat {
  userName: string;
  loginCount: number;
  conversationCount: number;
  messageCount: number;
}

interface UserStatsChartProps {
  data: UserGraphStat[];
}

const getInitials = (name: string) => {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("");
};

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const userData = payload[0].payload;
    return (
      <div className="bg-background p-2 border border-border rounded-md shadow-md">
        <p className="font-bold">{userData.userName}</p>
        {payload.map((entry: any) => (
          <p key={entry.name} style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const UserStatsChart: React.FC<UserStatsChartProps> = ({ data }) => {
  const chartConfig: ChartConfig = {
    loginCount: {
      label: "Logins",
      color: "var(--chart-1)",
    },
    conversationCount: {
      label: "Samtaler",
      color: "var(--chart-2)",
    },
    messageCount: {
      label: "Beskeder",
      color: "var(--chart-3)",
    },
  };

  const [visibleBars, setVisibleBars] = useState({
    loginCount: true,
    conversationCount: true,
    messageCount: true,
  });

  const handleLegendClick = (data: any) => {
    const { dataKey } = data;
    const dataKeyCasted = dataKey as keyof typeof visibleBars;
    if (dataKeyCasted) {
      setVisibleBars((prev) => ({
        ...prev,
        [dataKey]: !prev[dataKeyCasted],
      }));
    }
  };

  const dataWithInitials = data.map((item) => ({
    ...item,
    userInitials: getInitials(item.userName),
  }));

  const renderCustomTick = (tickProps: any) => {
    const { x, y, payload } = tickProps;
    const initials = payload.value.split("");
    return (
      <g transform={`translate(${x},${y})`}>
        {initials.map((char: string, index: number) => (
          <text
            key={index}
            x={0}
            y={index * 10}
            textAnchor="middle"
            fill="var(--color-muted-foreground)"
            fontSize={12}
          >
            {char}
          </text>
        ))}
      </g>
    );
  };

  return (
    <Card className="rounded-lg shadow-md">
      <CardHeader className="flex items-center gap-2 space-y-0 py-5 sm:flex-row">
        <div className="grid flex-1 gap-1 text-center sm:text-left">
          <CardTitle>Bruger interaktioner</CardTitle>
          <CardDescription>
            Overblik over brugernes interaktioner i perioden
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
        <ChartContainer
          config={chartConfig}
          className="aspect-auto h-[250px] w-full"
        >
          <BarChart
            accessibilityLayer
            data={dataWithInitials}
            layout="horizontal"
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="userInitials"
              type="category"
              tickLine={false}
              tickMargin={5}
              axisLine={false}
              interval={0} // Show all labels
              tick={renderCustomTick}
            />
            <YAxis type="number" />
            <Tooltip content={<CustomTooltip />} />
            <ChartLegend
              onClick={handleLegendClick}
              wrapperStyle={{ top: -30, right: 0 }}
            />
            {visibleBars.loginCount && (
              <Bar
                dataKey="loginCount"
                fill="var(--chart-1)"
                radius={4}
                name="Logins"
              />
            )}
            {visibleBars.conversationCount && (
              <Bar
                dataKey="conversationCount"
                fill="var(--chart-2)"
                radius={4}
                name="Samtaler"
              />
            )}
            {visibleBars.messageCount && (
              <Bar
                dataKey="messageCount"
                fill="var(--chart-3)"
                radius={4}
                name="Beskeder"
              />
            )}
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};

export default UserStatsChart;
